import { useEffect, useState } from 'react';

const THRESHOLD = 70;
const MAX_PULL = 110;
const RESISTANCE = 0.5;

function isStandalone(): boolean {
  const iosNavigator = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches || iosNavigator.standalone === true
  );
}

function isInsideScrolledElement(target: EventTarget | null): boolean {
  let element = target instanceof Element ? target : null;
  while (element && element !== document.body) {
    if (element.scrollTop > 0) return true;
    element = element.parentElement;
  }
  return false;
}

export default function usePullToRefresh() {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!isStandalone()) return undefined;

    let startY: number | null = null;
    let distance = 0;

    const reset = () => {
      startY = null;
      distance = 0;
      setPull(0);
    };

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (
        !touch ||
        event.touches.length > 1 ||
        window.scrollY > 0 ||
        window.getComputedStyle(document.body).overflowY === 'hidden' ||
        isInsideScrolledElement(event.target)
      ) {
        return;
      }
      startY = touch.clientY;
    };

    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (startY === null || !touch) return;
      const delta = touch.clientY - startY;
      distance = delta > 0 ? Math.min(delta * RESISTANCE, MAX_PULL) : 0;
      setPull(distance);
    };

    const onTouchEnd = () => {
      if (startY === null) return;
      if (distance >= THRESHOLD) {
        startY = null;
        setRefreshing(true);
        window.location.reload();
        return;
      }
      reset();
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', reset);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', reset);
    };
  }, []);

  return { pull, refreshing, threshold: THRESHOLD };
}
